import React, { useContext, useRef } from 'react';
import { useEffect, useState } from 'react';
import SETTING_KEYS from '../../constants/setting-keys';
import {
    getAllFieldstrength,
    getAllManufactures,
    getAllTypes,
    updateImplant,
    addManufacturer,
    addImplant,
    draftImplant,
    uploadImplantImage,
    extractDataFromPDF,
    uploadSourceFile,
    completeQueuedImplant,
} from '../../services/apiService';
import { getAllTasks } from '../../services/api/aiTaskService';
import { IAITask } from '../../models/aiTaskModel';
import Button, { ButtonType } from '../general-components/Button';
import SelectItem from '../general-components/SelectItem';
import AuthContext from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import LoadingRing from '../ui-components/LoadingRing';
import { useImplantContext } from '../../store/implant-context';
import TypeSearchField from './TypeSearchField';
import Alert from '../general-components/Alert';
import styles from './EditImplant.module.css';
import {
    AutofilledFieldsType,
    AutofilledFieldsKeys,
} from '../../utils/pdfExtractor';
import { normalizeManufacturer } from '../../utils/utils';
import CatalogNumbers from './regional/EditCatalogNumbers';
import Conditionals from '../conditionals/Conditionals';
import CustomFields from '../custom-fields/CustomFields';
import ImplantSource from './regional/ImplantSource';
import GeneralInfo from './GeneralInfo';
import FormInput from '../general-components/FormInput';
import Brand from './Brand';
import Region from './regional/Region';
import Model from './Model';
import Category from './Category';
import MriStatus from './MriStatus';
import RegionalInfo from './regional/RegionalInfo';
import RichText from '../general-components/RichText';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import FileExtraction from '../file-extraction/FileExtraction';

const EditImplant: React.FC<{
    id?: string;
    isNew: boolean;
    onSubmit?: Function;
}> = ({ isNew, onSubmit }) => {
    const userContext = useContext(AuthContext);
    const {
        dispatchConditionals,
        dispatchInfo,
        fileName,
        conditionals,
        info,
        regionalInfos,
        addConditional,
        updateRegionalInfoField
    } = useImplantContext();

    const [imageName, setImageName] = useState(info[SETTING_KEYS.image]);
    const [manufacturers, setManufacturers] = useState([]);
    const [fieldStrengths, setFieldStrengths] = useState([]);
    const [aiTasks, setAiTasks] = useState<IAITask[]>([]);
    const [image, setImage] = useState();
    const [types, setTypes] = useState<string[]>([]);
    const [showWarningIcon, setShowWarningIcon] = useState(false);
    const [isEnterCustomManufacturer, setIsEnterCustomManufacturer] =
        useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [isExtracting, setIsExtracting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [isError, setIsError] = useState(false);
    const [newImplantError, setNewImplantError] = useState(false);
    const [errorCond, setErrorCond] = useState('');
    const [extractionError, setExtractionError] = useState<string | null>(null);
    const [fileError, setFileError] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [autofilledFields, setAutofilledFields] =
        useState<AutofilledFieldsType>({});
    const [returnedError, setReturnedError] = useState('');
    const [showSavedMessage, setShowSavedMessage] = useState(false);
    const [notifyApprovers, setNotifyApprovers] = useState(false);
    const [conditionalValidationError, setConditionalValidationError] =
        useState('');

    const getManufacturers = async () => {
        const manufacturers = await getAllManufactures();
        setManufacturers(manufacturers);
    };

    const getData = async () => {
        const types = await getAllTypes();
        const fieldStrengths = await getAllFieldstrength();
        const tasks = await getAllTasks();
        getManufacturers();
        setTypes(types);
        setFieldStrengths(fieldStrengths);
        // Filter tasks that are relevant for file extraction (must accept file_url)
        const extractionTasks = tasks.filter((task) => 
            task.inputSchema?.properties?.file_url
        );
        setAiTasks(extractionTasks);
        setIsLoading(false);
    };

    useEffect(() => {
        getData();
    }, []);

    const handleActivePassiveChange = (value: string) => {
        updateInfoField(SETTING_KEYS.category, value);
    };

    // Reset extraction error and autofilled fields when the source changes
    useEffect(() => {
        setExtractionError('');
        setAutofilledFields({});
    }, [fileName]);

    type SaveState = 'draft' | 'versioned' | 'edits';

    const save = async (saveState: SaveState) => {
        setIsSaving(true);
        setNewImplantError(false);
        setSaved(false);
        const updatedInfo: any = {
            ...info,
            [SETTING_KEYS.type]: info[SETTING_KEYS.type],
            [SETTING_KEYS.manufacturer]: info[SETTING_KEYS.manufacturer],
            [SETTING_KEYS.mriStatus]: parseInt(info[SETTING_KEYS.mriStatus]),
            [SETTING_KEYS.image]: imageName,
            regionalInfos: regionalInfos,
        };

        let updRes;
        if (saveState === 'draft') {
            updRes = await draftImplant(updatedInfo, conditionals);
        } else if (saveState === 'versioned') {
            updRes = await updateImplant(updatedInfo, conditionals, true);
        } else if (saveState === 'edits') {
            updRes = await updateImplant(
                updatedInfo,
                conditionals,
                false,
                notifyApprovers
            );
        }

        if (updRes.status === 200) {
            if (fileName) {
                await completeQueuedImplant(fileName);
            }
        }
        if (updRes.status === 200 && image !== undefined && image !== null) {
            await uploadImplantImage(image, updRes.data);
        }
        if (updRes.status !== 200) {
            setReturnedError(updRes.data);
            setIsSaving(false);
        } else {
            setIsSaving(false);
            setSaved(true);
        }
    };

    const addNewImplant = async (isDraft: boolean) => {
        setIsSaving(true);
        setSaved(false);

        if (isEnterCustomManufacturer) {
            const res = await addManufacturer(info[SETTING_KEYS.manufacturer]);
            if (res.status !== 200) {
                setIsSaving(false);
                setNewImplantError(true);
                setReturnedError(res.data.error);
                setSaved(false);
                return;
            }
        }
        const res = await addImplant(
            {
                ...info,
                [SETTING_KEYS.type]: info[SETTING_KEYS.type],
                [SETTING_KEYS.manufacturer]: info[SETTING_KEYS.manufacturer],
                [SETTING_KEYS.mriStatus]: parseInt(
                    info[SETTING_KEYS.mriStatus]
                ),
                [SETTING_KEYS.editStatus]: isDraft ? 1 : 2,
                [SETTING_KEYS.filename]: fileName,
                [SETTING_KEYS.image]: imageName,
                regionalInfos: regionalInfos,
            },
            conditionals
        );

        if (res.status === 200) {
            if (fileName) {
                await completeQueuedImplant(fileName);
            }
        }

        if (res.status === 200 && image !== undefined && image !== null) {
            await uploadImplantImage(image, res.data);
        }
        setIsSaving(false);
        if (res.status !== 200) {
            setNewImplantError(true);
            setReturnedError(res.data.error);
            setSaved(false);
        } else {
            setSaved(true);
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        }
    };

    const handleOnSave = (saveState: SaveState) => {
        let errorFlag = false;
        let errorConStr = '';

        const hasIfu = regionalInfos.every(
            (regionalInfo) => regionalInfo.ifu !== null
        );
        if (!hasIfu) {
            errorFlag = true;
            if (!errorConStr.includes('IFU')) {
                errorConStr = errorConStr + ' IFU';
            }
        }

        // Check if all regional infos have model data
        const hasModel = regionalInfos.every(
            (regionalInfo) => regionalInfo.model && regionalInfo.model !== ''
        );
        if (!hasModel) {
            errorFlag = true;
            if (!errorConStr.includes('Model')) {
                errorConStr = errorConStr + ' Model';
            }
        }

        // Check if regional infos have region
        const hasRegion = regionalInfos.every(
            (regionalInfo) =>
                regionalInfo.regions && regionalInfo.regions.length > 0
        );
        if (!hasRegion) {
            errorFlag = true;
            if (!errorConStr.includes('Region')) {
                errorConStr = errorConStr + ' Region';
            }
        }

        // Check if all regional infos have source data
        const hasSource = regionalInfos.every(
            (regionalInfo) =>
                regionalInfo.sources &&
                regionalInfo.sources.length > 0 &&
                regionalInfo.sources[0].content !== ''
        );
        if (!hasSource) {
            errorFlag = true;
            if (!errorConStr.includes('Source')) {
                errorConStr = errorConStr + ' Source';
            }
        }

        if (info[SETTING_KEYS.category] === '') {
            errorFlag = true;
            if (!errorConStr.includes('Category')) {
                errorConStr = errorConStr + ' Category';
            }
        }

        if (
            !info[SETTING_KEYS.manufacturer] ||
            info[SETTING_KEYS.manufacturer].trim() === ''
        ) {
            errorFlag = true;
            if (!errorConStr.includes('Manufacturer')) {
                errorConStr = errorConStr + ' Manufacturer';
            }
        }

        if (
            isEnterCustomManufacturer &&
            info[SETTING_KEYS.manufacturer] === ''
        ) {
            errorFlag = true;
            if (!errorConStr.includes('Manufacturer')) {
                errorConStr = errorConStr + ' Manufacturer';
            }
        }

        if (!info[SETTING_KEYS.type] || info[SETTING_KEYS.type].trim() === '') {
            errorFlag = true;
            if (!errorConStr.includes('Type')) {
                errorConStr = errorConStr + 'Type';
            }
        }

        // Check for conditional validation errors
        if (conditionalValidationError) {
            errorFlag = true;
            errorConStr = errorConStr + conditionalValidationError;
        }

        errorConStr = errorConStr.slice(1).replaceAll(' ', ', ');
        setIsError(errorFlag);
        setErrorCond(errorConStr);

        if (!errorFlag && !isNew) {
            save(saveState);
        } else if (!errorFlag && isNew) {
            addNewImplant(saveState === 'draft');
        }
    };

    useEffect(() => {
        if (saved && onSubmit) {
            onSubmit();
            setShowSavedMessage(true);
            const timer = setTimeout(() => {
                setShowSavedMessage(false);
            }, 3000);
            setSaved(false);
            return () => clearTimeout(timer);
        }
    }, [saved]);

    const updateInfoField = (fieldName: any, value: any) => {
        dispatchInfo({
            type: 'UPDATE',
            payload: { name: fieldName, value: value },
        });
    };

    const updateConditionalField = (fieldName: any, value: any) => {
        // For backward compatibility, update first conditional
        // This uses the UPDATE action which updates the first conditional by default
        dispatchConditionals({
            type: 'UPDATE',
            payload: { name: fieldName, value: value },
        });
    };

    const updateType = (type: string) => {
        updateInfoField(SETTING_KEYS.type, type);
    };

    const handleExtractionComplete = (data: any) => {
        setIsExtracting(false);
        if (!data) return;

        // Naively update MRI Status to conditional for first regionalinfo
        // Map extracted data to conditional fields
        // We assume the AI task returns keys matching our SETTING_KEYS
        Object.entries(data).forEach(([key, value]) => {
            console.log(key, value);
            if (key === "MRI Status") {
                let status;
                switch (value) {
                    case 'MRI Safe':
                        status = 3;
                        break;
                    case 'MRI Unsafe':
                        status = 1;
                        break;
                    case 'MRI Unknown':
                        status = 0;
                        break;
                    case 'MRI Conditional':
                        status = 2;
                        break;
                    default:
                        status = -1; // or some default value
                }
                updateRegionalInfoField(0, 'mriStatus', status);
            } else if (key === "Model name") {
                updateRegionalInfoField(0, 'model', value);
            } else if (key === "Precaution") {
                updateRegionalInfoField(0, 'precaution', value);
            } else {
                updateConditionalField(key, value);
            }
            
        });

        setAutofilledFields(data);
    };

    useEffect(() => {
        const hasConditionalMriStatus = regionalInfos.some(
            (regionalInfo) => regionalInfo && regionalInfo.mriStatus === 2
        );
        const emptyConditionals = conditionals.length === 0;
        if (hasConditionalMriStatus && emptyConditionals) {
            addConditional();
        } else if (!hasConditionalMriStatus && conditionals.length > 0) {
            dispatchConditionals({ type: 'RESET' });
        }
    }, [regionalInfos]);

    return (
        <>
            <div className={`${styles.implant}`}>
                <div className={`${styles.flex} ${!isNew ? 'preview' : ''}`}>
                    <BackgroundContainer>
                        <GeneralInfo>
                            <div className={`${styles.info}`}>
                                <div className={styles.infoColumn}>
                                    <Brand
                                        fetchManufacturers={getManufacturers}
                                        manufacturers={manufacturers}
                                        isCustomManufacturer={
                                            isEnterCustomManufacturer
                                        }
                                        info={info[SETTING_KEYS.manufacturer]}
                                        manufacturerString={
                                            SETTING_KEYS.manufacturer
                                        }
                                        onUpdateInfoField={updateInfoField}
                                        toggleCustomManufacturer={
                                            setIsEnterCustomManufacturer
                                        }
                                    />
                                    <Category
                                        isStent={
                                            info[SETTING_KEYS.category] ===
                                            'Stent'
                                                ? true
                                                : false
                                        }
                                        info={info}
                                        onActivePassiveToggle={
                                            handleActivePassiveChange
                                        }
                                        onUpdateInfoField={updateInfoField}
                                    />

                                    <TypeSearchField
                                        title={
                                            <>
                                                Type
                                                <span
                                                    style={{
                                                        color: 'red',
                                                        marginLeft: '5px',
                                                    }}
                                                >
                                                    *
                                                </span>
                                            </>
                                        }
                                        placeholder="Search type"
                                        types={types}
                                        updateType={updateType}
                                        currentValue={info[SETTING_KEYS.type]}
                                    />
                                    <label className={styles.infoLabel}>
                                        Body placement
                                    </label>
                                    <input
                                        name={SETTING_KEYS.bodyPlacement}
                                        value={
                                            Array.isArray(
                                                info[SETTING_KEYS.bodyPlacement]
                                            )
                                                ? info[
                                                      SETTING_KEYS.bodyPlacement
                                                  ].join(',')
                                                : info[
                                                      SETTING_KEYS.bodyPlacement
                                                  ]
                                        }
                                        type="text"
                                        placeholder="Enter body placement"
                                        onChange={(e) =>
                                            updateInfoField(
                                                SETTING_KEYS.bodyPlacement,
                                                e.target.value.split(',')
                                            )
                                        }
                                    />
                                </div>

                                <div className={styles.infoColumn}>
                                    <label className={styles.infoLabel}>
                                        GMDN definition
                                    </label>
                                    <textarea
                                        name={SETTING_KEYS.gmdnDefinition}
                                        placeholder="Provide GMDN definition"
                                        value={
                                            info[SETTING_KEYS.gmdnDefinition]
                                        }
                                        onChange={(e) =>
                                            updateInfoField(
                                                SETTING_KEYS.gmdnDefinition,
                                                e.target.value
                                            )
                                        }
                                    />
                                    <RichText
                                        label={SETTING_KEYS.notes}
                                        description={info[SETTING_KEYS.notes]}
                                        onChange={(richText: any) => {
                                            updateInfoField(
                                                SETTING_KEYS.notes,
                                                richText
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        </GeneralInfo>
                    </BackgroundContainer>

                    <div className={styles.flex} style={{ margin: '25px 0' }}>
                        <FileExtraction
                                label={`Extract Data from IFU`}
                                ownerType="manufacturer"
                                manufacturerName={info[SETTING_KEYS.manufacturer]}
                                allowedTypes={{
                                    document: true,
                                    image: false,
                                    video: false,
                                }}
                                onExtractionStart={() => setIsExtracting(true)}
                                onExtractionComplete={handleExtractionComplete}
                                disabled={!info[SETTING_KEYS.manufacturer]}
                                disabledMessage="Please select a manufacturer above to enable file extraction."
                                aiTasks={aiTasks}
                        />
                    </div>
                    
                    <div className={styles.info}>
                        <RegionalInfo />

                        <Conditionals
                            fieldStrengths={fieldStrengths}
                            autofilledFields={autofilledFields}
                            showWarningIcon={showWarningIcon}
                            onSetShowWarningIcon={setShowWarningIcon}
                            onValidationError={setConditionalValidationError}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.save}>
                <Button
                    type={ButtonType.primary}
                    onClick={() => handleOnSave('draft')}
                >
                    Save draft
                </Button>
                {!isNew && (
                    <Button
                        type={ButtonType.submit}
                        onClick={() => handleOnSave('edits')}
                        disabled={
                            extractionError !== '' || showWarningIcon || isNew
                        }
                    >
                        Save Edits
                    </Button>
                )}
                <Button
                    type={ButtonType.submit}
                    onClick={() => handleOnSave('versioned')}
                    disabled={extractionError !== '' || showWarningIcon}
                >
                    {isNew ? 'Save and Complete' : 'Save as New Version'}
                </Button>
            </div>
            {!isNew && (
                <SelectItem
                    labelled={true}
                    type="checkbox"
                    value="Notify Approvers of Update (only applicable for 'Save Edits')"
                    onChange={(value, checked) => {
                        setNotifyApprovers(checked);
                    }}
                />
            )}
            {fileError && (
                <div style={{ color: 'red' }}>
                    Please provide the IFU or source of the data
                </div>
            )}

            {isError && (
                <div>
                    Please check empty fields: {errorCond}. Delete empty or fill
                    them before saving.
                </div>
            )}
            {isSaving && <div>Saving...</div>}
            {showSavedMessage && <div>Implant has been Saved</div>}
            {returnedError && <div>{returnedError}</div>}
            {isNew && newImplantError && <div>{returnedError}</div>}
        </>
    );
};

export default EditImplant;
